import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Popconfirm,
  message,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  StopOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import quotationItemTemplateService, {
  QuotationItemTemplate
} from '../../../../services/quotationItemTemplateService';
import QuotationItemTemplateForm from './QuotationItemTemplateForm';
import { useTranslation } from "react-i18next";

const { Option } = Select;

const QuotationItemTemplateList: React.FC = () => {
    const { t } = useTranslation();
  const [templates, setTemplates] = useState<QuotationItemTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<QuotationItemTemplate | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>(undefined);
  const [categories, setCategories] = useState<string[]>([]);
  const [stats, setStats] = useState({
    totalTemplates: 0,
    activeTemplates: 0,
    inactiveTemplates: 0,
    categoriesCount: 0
  });

  useEffect(() => {
    loadTemplates();
    loadCategories();
    loadStats();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (searchTerm) filters.searchTerm = searchTerm;
      if (categoryFilter) filters.category = categoryFilter;
      if (statusFilter !== undefined) filters.isActive = statusFilter;

      const data = await quotationItemTemplateService.getAllTemplates(filters);
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
      message.error(t('Failed to load quotation item templates'));
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await quotationItemTemplateService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadStats = async () => {
    try {
      const data = await quotationItemTemplateService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleCreate = () => {
    setSelectedTemplate(null);
    setFormVisible(true);
  };

  const handleEdit = (template: QuotationItemTemplate) => {
    setSelectedTemplate(template);
    setFormVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await quotationItemTemplateService.deleteTemplate(id);
      message.success(t('Template deleted successfully'));
      loadTemplates();
      loadStats();
    } catch (error: any) {
      message.error(t('Failed to delete template: {{message}}', { message: error.message }));
    }
  };

  const handleToggleStatus = async (template: QuotationItemTemplate) => {
    try {
      if (template.isActive) {
        await quotationItemTemplateService.deactivateTemplate(template.id);
        message.success(t('Template deactivated'));
      } else {
        await quotationItemTemplateService.activateTemplate(template.id);
        message.success(t('Template activated'));
      }
      loadTemplates();
      loadStats();
    } catch (error: any) {
      message.error(t('Failed to update status: {{message}}', { message: error.message }));
    }
  };

  const handleFormSuccess = () => {
    loadTemplates();
    loadCategories();
    loadStats();
  };

  const handleSearch = () => {
    loadTemplates();
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setCategoryFilter(undefined);
    setStatusFilter(undefined);
    setTimeout(() => loadTemplates(), 0);
  };

  const columns: ColumnsType<QuotationItemTemplate> = [
    {
      title: t('Item Title'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: '15%',
      render: (title) => <strong>{title}</strong>
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      width: '35%'
    },
    {
      title: t('Category'),
      dataIndex: 'category',
      key: 'category',
      width: '15%',
      render: (category) => category ? <Tag color="blue">{category}</Tag> : '-'
    },
    {
      title: t('Unit Price'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: '12%',
      align: 'right',
      render: (price) => `$${Number(price).toFixed(2)}`
    },
    {
      title: t('Status'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: '10%',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? t('Active') : t('Inactive')}
        </Tag>
      )
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 280,
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="default"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('Edit')}
                            </Button>
          <Button
            type="default"
            size="small"
            icon={record.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleToggleStatus(record)}
          >
            {record.isActive ? t('Deactivate') : t('Activate')}
          </Button>
          <Popconfirm
            title={t('Are you sure you want to delete this template?')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('Yes')}
            cancelText={t('No')}
          >
            <Button
              type="default"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              {t('Delete')}
                                  </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('Total Templates')}
              value={stats.totalTemplates}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('Active')}
              value={stats.activeTemplates}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('Inactive')}
              value={stats.inactiveTemplates}
              valueStyle={{ color: '#8c8c8c' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('Categories')}
              value={stats.categoriesCount}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={t('Quotation Item Templates')}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            {t('Create Template')}
                          </Button>
        }
      >
        <Space style={{ marginBottom: 16, width: '100%' }} direction="vertical">
          <Row gutter={8}>
            <Col flex="auto">
              <Input
                placeholder={t('Search by item title or description')}
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            </Col>
            <Col>
              <Select
                placeholder={t('Category')}
                style={{ width: 150 }}
                value={categoryFilter}
                onChange={setCategoryFilter}
                allowClear
              >
                {categories.map((cat) => (
                  <Option key={cat} value={cat}>
                    {cat}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Select
                placeholder={t('Status')}
                style={{ width: 120 }}
                value={statusFilter}
                onChange={setStatusFilter}
                allowClear
              >
                <Option value={true}>{t('Active')}</Option>
                <Option value={false}>{t('Inactive')}</Option>
              </Select>
            </Col>
            <Col>
              <Button type="primary" onClick={handleSearch}>
                {t('Search')}
                                            </Button>
            </Col>
            <Col>
              <Button onClick={handleResetFilters}>
                {t('Reset')}
                                            </Button>
            </Col>
          </Row>
        </Space>

        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => t('Total {{total}} templates', { total })
          }}
        />
      </Card>

      <QuotationItemTemplateForm
        visible={formVisible}
        template={selectedTemplate}
        onClose={() => setFormVisible(false)}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
};

export default QuotationItemTemplateList;